import { useLocation, useNavigate } from 'react-router-dom';
import { TileColumn } from '@ofself/shell';
import { Search } from 'lucide-react';
import EcoShell, { usePortalChrome } from '../EcoShell';
import DevInbox from './DevInbox';
import { useGroup } from '../../contexts/GroupContext';
import { placeOf, appPlaceOf } from '../../devmap';
import { PageStrip, useIsPhone } from './PhoneNav';
import '../../styles/Frame.css';

/**
 * The portal's frame, as Nucleus stands it (frontend/src/components/Layout.js):
 * the rail's doors are EcoShell's; here is the SECTION COLUMN — the section's
 * pages as tiles, each with its one-line "about", the page you are on lit —
 * and the DESK beside it. A section with one page goes straight to the desk.
 *
 * The desk has no header band: a transparent strip carries where you are
 * (the space, the section, the page), and the page brings its own title and
 * verbs beneath it.
 */
export function LocationLine({ spaceName, section, page }) {
  const { openSearch, signedIn } = usePortalChrome();
  return (
    <div className="dev-head" aria-label="Where you are">
      <span className="dev-head__where">
        <span className="dev-head__space">{spaceName}</span>
        {section && <span className="dev-head__section">{section.label}</span>}
        {section && page && page.label !== section.label && <span className="dev-head__page">{page.label}</span>}
        {!section && <span className="dev-head__page">{page?.label || 'Home'}</span>}
      </span>
      {/* Search and the inbox, top right on every page, as the mockup has them. */}
      <span className="dev-head__acts">
        {openSearch && (
          <button type="button" className="dev-head__search" onClick={openSearch} aria-label="Search the portal">
            <Search size={13} aria-hidden="true" />
            <span>Search</span>
            <kbd>⌘K</kbd>
          </button>
        )}
        {/* The inbox answers rows where they stand (DevInbox); the kit's
            InboxSeat only lists and marks read. A guest has no inbox. */}
        {signedIn && <DevInbox />}
      </span>
    </div>
  );
}

function PortalLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeGroup } = useGroup();
  const place = placeOf(location.pathname, location.search);
  const inApp = appPlaceOf(location.pathname, location.search);
  const phone = useIsPhone();

  // Inside one app the app lists its own pages (AppPageNav); the location line
  // names the app's page.
  const { section } = place;
  // Home is the one page outside every section; any other unmapped address is
  // the not-found page, and the line must not claim it is Home.
  const lost = !section && location.pathname.replace(/\/+$/, '') !== '/home';
  const page = inApp ? inApp.page : lost ? { label: 'Not found' } : place.page;

  const desk = (
    <main className="portal-main block block--fill ofself-desk dev-desk" data-section={section?.id || 'home'}>
      <LocationLine spaceName={activeGroup ? activeGroup.name : 'Personal'} section={section} page={page} />
      {phone && section && <PageStrip pages={section.pages} active={inApp ? 'apps' : page?.id} onSelect={(p) => navigate(p.to)} />}
      <div className="block__body portal-main-body">{children}</div>
    </main>
  );

  // On a phone the page strip (above) stands in for the column, so the column
  // and its fold-to-page back button are not mounted at all.
  if (!section || section.pages.length < 2 || phone) return <EcoShell>{desk}</EcoShell>;

  return (
    <EcoShell>
      <TileColumn
        label={section.label}
        items={section.pages.map((p) => ({
          id: p.id,
          label: <span className="nx-tile">{p.label}<span className="nx-tile__about">{p.about}</span></span>,
        }))}
        active={inApp ? 'apps' : page?.id}
        onSelect={(id) => {
          const next = section.pages.find((p) => p.id === id);
          if (next) navigate(next.to);
        }}
        storageKey="dev:section-col"
        className="dev-section"
        mobileIndex={1}
        trailing={desk}
      />
    </EcoShell>
  );
}

export default PortalLayout;
